import { useCallback, useEffect, useMemo, useRef } from "react"

import { useAgentNamesTransport } from "@/agents/transport"
import { useMetricDirectory } from "@/metrics/registry"

import type { WidgetExportData } from "../../components/BaseWidget"
import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../../routes/dashboardBatchTransportQuery"
import type { ChartWidgetConfig, StatisticDataResult } from "../../types/chart"
import { VISUALIZATION_TYPE } from "../../types/core"
import {
  getChartAgentIds,
  resolveChartTargetCondition,
  type DashboardChartRow,
} from "../../utils/chart/chartWidgetDataState"
import {
  buildChartData,
  buildDashboardMetricQueryInput,
  buildDashboardStatisticQueryInput,
  buildHumanReviewMetricQueryInput,
  createChartExportDataKey,
  getChartBucketInterval,
  getChartQueryResult,
  getChartTargetValues,
  getChartZoomOverride,
  getEffectiveChartDateRange,
  getIsWholeRangeBarVisualization,
  hasChartSuccessCriteriaError,
  isMetricBinary,
  shouldEnableFlexibleChartQuery,
  shouldEnableHumanReviewChartQuery,
  shouldEnableStatisticChartQuery,
} from "../../utils/chart/chartWidgetState"
import {
  mergeAgentNameLookups,
  useDisplayNameLookups,
} from "../../utils/chart/displayNameUtils"
import { calculateSplitKeys } from "../../utils/chart/splitKeys"
import { getBrowserTimezone } from "../../utils/chart/timezoneUtils"
import {
  isHumanReviewSource,
  isUploadedConversationSource,
  resolveDataSource,
} from "../../utils/dataSource"
import {
  getDashboardBatchRows,
  isTransportDashboardQueryLoading,
  serializeDashboardBatchQuery,
} from "../../utils/transportDashboardQueries"
import { useGlobalDateRangeOverride } from "../useGlobalDateRangeOverride"
import { useChartBaselines } from "./useChartBaselines"
import {
  useChartDataConfig,
  useResolvedChartConfig,
} from "./useChartDataConfig"
import { useChartMetric } from "./useChartMetric"

interface UseChartWidgetDataParams {
  config: ChartWidgetConfig
  onExportDataReady?: (data: WidgetExportData | null) => void
  onSplitKeysChange?: (splitKeys: string[]) => void
}

const CHART_QUERY_STALE_TIME = 5 * 60 * 1000
const CHART_QUERY_GC_TIME = 15 * 60 * 1000

export const useChartWidgetData = ({
  config: savedConfig,
  onExportDataReady,
  onSplitKeysChange,
}: UseChartWidgetDataParams) => {
  const {
    isMetricLoading,
    isMetricMissing,
    metric,
    metricCatalogError,
    metricRefreshError,
    metricFilterError,
    metricDefinitionError,
    recheckMetricCatalog,
  } = useChartMetric(savedConfig.metricId, savedConfig.metricFilter)
  const metricQueriesReady =
    Boolean(metric) &&
    !isMetricLoading &&
    !metricFilterError &&
    !metricDefinitionError
  const config = useResolvedChartConfig(savedConfig, metric)
  const { state } = useDashboardFilters()
  const { lookups, isLoading: lookupsLoading } = useDisplayNameLookups()
  const { setCustomGlobalOverride } = useGlobalDateRangeOverride()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const handleChartZoom = useCallback(
    (range: { start: Date; end: Date }) => {
      setCustomGlobalOverride(
        getChartZoomOverride(range, config.bucketInterval),
      )
    },
    [setCustomGlobalOverride, config.bucketInterval],
  )

  const { byManager } = useMetricDirectory()
  const metricIsBinary = useMemo(
    () => isMetricBinary(metric, byManager[metric?.metric_manager ?? ""]),
    [metric, byManager],
  )

  const effectiveTargetCondition = useMemo(
    () => resolveChartTargetCondition(metric, metricIsBinary),
    [metric, metricIsBinary],
  )

  const effectiveDateRange = useMemo(
    () =>
      getEffectiveChartDateRange({
        globalDateRange,
        globalDateRangeOverride,
        isOverrideActive,
      }),
    [globalDateRange, globalDateRangeOverride, isOverrideActive],
  )

  const bucketInterval = getChartBucketInterval(config.bucketInterval)
  const timezone = getBrowserTimezone()

  const baselineData = useChartBaselines(
    config,
    bucketInterval,
    timezone,
    effectiveDateRange,
  )

  const isHistogramVisualization =
    config.visualizationType === VISUALIZATION_TYPE.HISTOGRAM
  const isTopListVisualization =
    config.visualizationType === VISUALIZATION_TYPE.TOP_LIST
  const isWholeRangeVisualization = getIsWholeRangeBarVisualization(config)
  const isStatisticVisualization =
    config.visualizationType === VISUALIZATION_TYPE.STATISTIC
  const dataSource = resolveDataSource(config)
  const isMonitoring = isUploadedConversationSource(dataSource)
  const isHumanReview = isHumanReviewSource(dataSource)

  const flexibleQueryEnabled = shouldEnableFlexibleChartQuery({
    aggregation: config.aggregation,
    effectiveDateRange,
    effectiveTargetCondition,
    isHumanReview,
    metricId: config.metricId,
    metricIsBinary,
    metricOutputType: config.metricOutputType,
  })
  const statisticQueryEnabled = shouldEnableStatisticChartQuery({
    effectiveDateRange,
    isHumanReview,
    isStatisticVisualization,
    metricId: config.metricId,
  })
  const humanReviewQueryEnabled = shouldEnableHumanReviewChartQuery({
    effectiveDateRange,
    isHumanReview,
    metricId: config.metricId,
  })

  const flexibleQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "flexible",
          kind: "queryDashboardMetric",
          ...buildDashboardMetricQueryInput({
            bucketInterval,
            config,
            effectiveDateRange,
            effectiveTargetCondition,
            isHistogramVisualization,
            isMonitoring,
            isTopListVisualization,
            isWholeRangeVisualization,
            metricId: metric?.id,
            timezone,
          }),
        }),
      ],
    }),
    [
      bucketInterval,
      config,
      effectiveDateRange,
      effectiveTargetCondition,
      isHistogramVisualization,
      isMonitoring,
      isTopListVisualization,
      isWholeRangeVisualization,
      metric?.id,
      timezone,
    ],
  )

  const statisticQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "statistic",
          kind: "queryDashboardStatistic",
          ...buildDashboardStatisticQueryInput({
            config,
            effectiveDateRange,
            effectiveTargetCondition,
            isMonitoring,
            metricId: metric?.id,
          }),
        }),
      ],
    }),
    [
      config,
      effectiveDateRange,
      effectiveTargetCondition,
      isMonitoring,
      metric?.id,
    ],
  )

  const humanReviewQueryRequest = useMemo(
    () => ({
      queries: [
        serializeDashboardBatchQuery({
          query_id: "human_review",
          kind: "queryHumanReviewMetric",
          ...buildHumanReviewMetricQueryInput({
            bucketInterval,
            config,
            effectiveDateRange,
            timezone,
          }),
        }),
      ],
    }),
    [bucketInterval, config, effectiveDateRange, timezone],
  )

  const flexibleQuery = useDashboardBatchTransportQuery({
    request: flexibleQueryRequest,
    options: {
      enabled: metricQueriesReady && flexibleQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const statisticQuery = useDashboardBatchTransportQuery({
    request: statisticQueryRequest,
    options: {
      enabled: metricQueriesReady && statisticQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const humanReviewQuery = useDashboardBatchTransportQuery({
    request: humanReviewQueryRequest,
    options: {
      enabled: metricQueriesReady && humanReviewQueryEnabled,
      staleTime: CHART_QUERY_STALE_TIME,
      gcTime: CHART_QUERY_GC_TIME,
    },
  })

  const flexibleRows = useMemo(
    () =>
      getDashboardBatchRows<DashboardChartRow>(
        metricQueriesReady ? flexibleQuery.data : undefined,
      ),
    [flexibleQuery.data, metricQueriesReady],
  )
  const statisticRows = useMemo(
    () =>
      getDashboardBatchRows<StatisticDataResult>(
        metricQueriesReady ? statisticQuery.data : undefined,
      ),
    [statisticQuery.data, metricQueriesReady],
  )
  const humanReviewRows = useMemo(
    () =>
      getDashboardBatchRows<DashboardChartRow>(
        metricQueriesReady ? humanReviewQuery.data : undefined,
      ),
    [humanReviewQuery.data, metricQueriesReady],
  )

  const humanReviewData = useMemo(() => {
    if (!isHumanReview || humanReviewRows.length === 0) {
      return null
    }
    return humanReviewRows
  }, [humanReviewRows, isHumanReview])

  const { data, error, isLoading, statisticData } = getChartQueryResult({
    flexibleError: flexibleQuery.error,
    flexibleLoading: isTransportDashboardQueryLoading(
      flexibleQuery,
      metricQueriesReady && flexibleQueryEnabled,
    ),
    flexibleQueryData: flexibleRows,
    humanReviewData,
    humanReviewError: humanReviewQuery.error,
    humanReviewLoading: isTransportDashboardQueryLoading(
      humanReviewQuery,
      metricQueriesReady && humanReviewQueryEnabled,
    ),
    isHumanReview,
    isStatisticVisualization,
    statisticData: statisticRows,
    statisticError: statisticQuery.error,
    statisticLoading: isTransportDashboardQueryLoading(
      statisticQuery,
      metricQueriesReady && statisticQueryEnabled,
    ),
  })

  const uniqueAgentIds = useMemo(
    () =>
      getChartAgentIds({
        dimension: config.groupBy || config.splitBy,
        flexibleRows: data,
        statisticRows,
      }),
    [config.groupBy, config.splitBy, data, statisticRows],
  )

  const { data: directAgentNames, isLoading: isDirectAgentNamesLoading } =
    useAgentNamesTransport(uniqueAgentIds)

  const displayNameLookups = useMemo(
    () => mergeAgentNameLookups(lookups, directAgentNames),
    [directAgentNames, lookups],
  )

  const chartDataConfig = useChartDataConfig(config)
  const chartData = useMemo(
    () =>
      buildChartData({
        bucketInterval,
        config: chartDataConfig,
        data,
        effectiveDateRange,
        isHistogramVisualization,
        isTopListVisualization,
        isWholeRangeVisualization,
      }),
    [
      bucketInterval,
      chartDataConfig,
      data,
      effectiveDateRange,
      isHistogramVisualization,
      isTopListVisualization,
      isWholeRangeVisualization,
    ],
  )

  const targetValues = useMemo(() => getChartTargetValues(metric), [metric])
  const splitKeys = useMemo(
    () => calculateSplitKeys(chartData, chartDataConfig, targetValues),
    [chartData, chartDataConfig, targetValues],
  )

  const prevSplitKeysRef = useRef<string[]>([])
  useEffect(() => {
    if (!onSplitKeysChange) {
      return
    }

    const previousSplitKeys = prevSplitKeysRef.current
    const hasChanged =
      splitKeys.length !== previousSplitKeys.length ||
      splitKeys.some((key, index) => key !== previousSplitKeys[index])

    if (!hasChanged) {
      return
    }

    prevSplitKeysRef.current = splitKeys
    onSplitKeysChange(splitKeys)
  }, [splitKeys, onSplitKeysChange])

  const stableDateRange = useMemo(
    () => ({
      start: effectiveDateRange.start,
      end: effectiveDateRange.end,
    }),
    [effectiveDateRange],
  )

  const exportDataKey = useMemo(
    () =>
      createChartExportDataKey({
        chartData,
        splitKeys,
        lookups: displayNameLookups,
        stableDateRange,
      }),
    [chartData, splitKeys, displayNameLookups, stableDateRange],
  )

  const prevExportDataRef = useRef<string | null>("")
  useEffect(() => {
    if (!onExportDataReady) {
      return
    }

    // The export follows the metric as well as the rows: a chart whose metric
    // no longer exists offers nothing to download.
    const currentExportKey = !metricQueriesReady ? null : exportDataKey
    if (prevExportDataRef.current === currentExportKey) {
      return
    }

    prevExportDataRef.current = currentExportKey
    if (!metricQueriesReady || chartData.length === 0) {
      onExportDataReady(null)
      return
    }

    onExportDataReady({
      widgetType: "chart",
      chartData,
      splitKeys,
      displayNameLookups,
      dateRange: stableDateRange,
    })
  }, [
    chartData,
    displayNameLookups,
    exportDataKey,
    metricQueriesReady,
    onExportDataReady,
    splitKeys,
    stableDateRange,
  ])

  const showSuccessCriteriaError =
    !metricDefinitionError &&
    hasChartSuccessCriteriaError({
      config,
      effectiveTargetCondition,
      isMetricLoading,
      metricIsBinary,
    })

  return {
    chartData,
    resolvedConfig: config,
    data,
    error:
      !isMetricLoading && (metricDefinitionError || metricFilterError)
        ? new Error(
            metricDefinitionError ||
              metricFilterError ||
              "Metric definition unavailable",
          )
        : error,
    handleChartZoom,
    isLoading,
    isMetricLoading,
    isMetricMissing,
    lookups: displayNameLookups,
    metricCatalogError,
    metricRefreshError,
    metricFilterError,
    metricDefinitionError,
    recheckMetricCatalog,
    lookupsLoading: lookupsLoading || isDirectAgentNamesLoading,
    metric,
    showSuccessCriteriaError,
    splitKeys,
    stableDateRange,
    baselineData,
    statisticData,
  }
}
